import { Fragment, Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { bindAll, isEmpty } from 'underscore';
import I18n from 'I18n';
import Icon from 'ContentComponents/cmv2/iconPicker/Icon';
import HelpTooltip from 'ContentComponents/cmv2/HelpTooltip';
import UISelect from 'UIComponents/input/UISelect';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import UICheckbox from 'UIComponents/input/UICheckbox';
import { SelectableBox } from 'foundations-components/transitional/components/SelectableBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import SanitizedLabel from 'ContentComponents/cmv2/SanitizedLabel';
import { SpacedDiv } from 'ContentComponents/cmv2/components/SpacedComponent';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import * as IconsActions from 'ContentData/actions/Icons';
import { makeGetIconData } from 'ContentData/selectors/Icons';
import DisplayTypes from 'ContentUtils/constants/CustomWidgetFieldDisplayTypes';
import { CHOICE } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { clean } from 'ContentComponents/cmv2/sanitize/sanitize';
import { isNullOrUndefined, isObject } from 'ContentUtils/helpers/ObjectHelpers';
import { getChoiceButtonPresets } from 'ContentUtils/constants/ChoicePresets';
import { DEFAULT_ICON_SET } from 'ContentUtils/constants/IconSets';
import { View } from 'foundations-components/components/View';
import ReorderChoicesButton from '../components/ReorderChoicesButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.choice.${key}`;
const DISPLAY_TYPES = DisplayTypes[CHOICE];
const ICON_BUTTON_SIZE = 45;
const ICON_SIZE = 35;
const IconWrapper = styled.div.withConfig({
  displayName: "Choice__IconWrapper",
  componentId: "sc-1b1cp09-0"
})(["display:flex;height:100%;align-items:center;justify-content:center;"]);
const ButtonWrapper = styled.div.withConfig({
  displayName: "Choice__ButtonWrapper",
  componentId: "sc-1b1cp09-1"
})(["width:33.3%;"]);
const ButtonImage = styled.img.withConfig({
  displayName: "Choice__ButtonImage",
  componentId: "sc-1b1cp09-2"
})(["height:auto;max-height:45px;max-width:100%;"]);
function makeMapStateToProps() {
  const getIconData = makeGetIconData();
  return function mapStateToProps(state) {
    return getIconData(state);
  };
}
const mapDispatchToProps = {
  fetchIcon: IconsActions.fetchIcon
};
const connector = connect(makeMapStateToProps, mapDispatchToProps);
class ChoiceField extends Component {
  constructor(props) {
    super(props);
    bindAll(this, 'onChangeCheckbox', 'onChangeRadio', 'onChangeValue', 'onSelectOpenChange', 'renderRadioOption', 'renderSelectOption');
    const optionsData = this.getOptionsData();
    this.choiceButtonPresets = getChoiceButtonPresets();
    this.state = {
      isOpenSelect: false,
      options: optionsData.options,
      optionsCache: optionsData.optionsCache
    };
  }
  componentDidMount() {
    const {
      choices,
      display = DISPLAY_TYPES.SELECT,
      fetchIcon,
      preset
    } = this.props;
    choices.forEach(choice => {
      const labelOrPreset = choice[1];
      const choiceLabelProperties = choice[2] || {};
      const buttonPresets = preset && this.choiceButtonPresets[preset];
      const labelProperties = display === DISPLAY_TYPES.BUTTONS && preset && buttonPresets ? buttonPresets[labelOrPreset.toLowerCase()] : choiceLabelProperties;
      if (labelProperties && labelProperties.icon) {
        const iconSet = labelProperties.set || DEFAULT_ICON_SET;
        fetchIcon(iconSet, {
          search: labelProperties.icon
        }).catch(err => {
          throw err;
        });
      }
    });
  }
  componentDidUpdate(prevProps) {
    const {
      choices
    } = this.props;
    if (JSON.stringify(prevProps.choices) !== JSON.stringify(choices)) {
      const optionsData = this.getOptionsData(this.state.optionsCache);
      this.setState({
        options: optionsData.options,
        optionsCache: optionsData.optionsCache
      });
    }
  }
  get seleniumKey() {
    const {
      fieldKey = []
    } = this.props;
    return fieldKey.join('.');
  }
  onChangeValue(e) {
    const {
      onFieldChange
    } = this.props;
    onFieldChange(e.target.value);
  }
  onChangeRadio(value) {
    const {
      onFieldChange
    } = this.props;
    onFieldChange(value);
  }
  onChangeCheckbox(choiceValue, fieldValue, {
    target: {
      checked
    }
  }) {
    const {
      onFieldChange
    } = this.props;
    const optionDeselected = !checked && fieldValue;
    const newOptionSelected = checked && fieldValue && !fieldValue.includes(choiceValue);
    let newFieldValue = fieldValue || [choiceValue];
    if (newOptionSelected) {
      newFieldValue = [...fieldValue, choiceValue];
    } else if (optionDeselected) {
      newFieldValue = fieldValue.filter(val => val !== choiceValue);
    }
    onFieldChange(newFieldValue);
  }
  onSelectOpenChange(e) {
    this.setState({
      isOpenSelect: e.target.value
    });
  }
  getOptionsData(optionsCache = {}) {
    const {
      choices: fieldChoices,
      display = DISPLAY_TYPES.SELECT,
      readOnly = false,
      value
    } = this.props;
    const choices = value && value.choices ? value.choices : fieldChoices;
    const hasCategories = choices.length && Array.isArray(choices[0][1]);
    let choiceOptions = [];
    const newOptionsCache = Object.assign({}, optionsCache);
    if (hasCategories && display === DISPLAY_TYPES.SELECT) {
      choiceOptions = choices.map(groupedChoice => {
        const [groupLabel, groupedChoices] = groupedChoice;
        const options = groupedChoices.map(choiceArray => {
          const [choiceValue, text] = choiceArray;
          const optObject = {
            disabled: readOnly,
            text,
            value: choiceValue
          };
          newOptionsCache[choiceValue] = optObject;
          return optObject;
        });
        return {
          disabled: readOnly,
          options,
          text: groupLabel
        };
      });
    } else {
      choiceOptions = choices.map(choiceArray => {
        const [choiceValue, text] = choiceArray;
        const choiceOption = {
          disabled: readOnly,
          text,
          value: choiceValue
        };
        newOptionsCache[choiceValue] = choiceOption;
        return choiceOption;
      });
    }
    return {
      options: choiceOptions,
      optionsCache: newOptionsCache
    };
  }
  getButtonFlexJustificationByIndex(index) {
    switch (index % 3) {
      case 1:
        return 'center';
      case 2:
        return 'end';
      default:
        return 'start';
    }
  }
  renderIcon(iconName, iconColor) {
    const {
      selectedIcons = {}
    } = this.props;
    const fontAwesomeIcon = selectedIcons[iconName];
    if (!fontAwesomeIcon) return null;
    const iconData = !!fontAwesomeIcon && {
      name: iconName,
      viewPort: fontAwesomeIcon.viewPort,
      svg: fontAwesomeIcon.svg
    };
    return /*#__PURE__*/_jsx(SpacedDiv, {
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(Icon, {
        color: iconColor,
        height: ICON_SIZE,
        iconData: iconData,
        width: ICON_SIZE
      })
    });
  }
  renderImage(imageUrl, imageAlt) {
    if (!imageUrl) return null;
    return /*#__PURE__*/_jsx(ButtonImage, {
      alt: imageAlt,
      src: imageUrl
    });
  }
  renderHelpText(helpText) {
    if (!helpText) return null;
    return /*#__PURE__*/_jsx(SpacedDiv, {
      marginInlineStart: "100",
      children: /*#__PURE__*/_jsx(HelpTooltip, {
        title: /*#__PURE__*/_jsx(SanitizedLabel, {
          label: helpText
        })
      })
    });
  }
  renderRadioOption(opt, radioName) {
    const {
      value: fieldValue,
      readOnly = false
    } = this.props;
    const [choiceValue, choiceLabel] = opt;
    return /*#__PURE__*/_jsx(UIRadioInput, {
      checked: choiceValue === fieldValue,
      "data-selenium": `${this.seleniumKey}:opt:${choiceValue}`,
      name: radioName,
      onChange: () => this.onChangeRadio(choiceValue),
      readOnly: readOnly,
      size: "small",
      value: choiceValue,
      children: /*#__PURE__*/_jsx(SanitizedLabel, {
        label: choiceLabel
      })
    }, choiceValue);
  }
  renderRadio() {
    // In the layout editor we want to display 2 edit forms next to each other
    // when in expanded mode. If there's a choice field we want each field instance
    // to have a completely unique radio field name, because otherwise the radios
    // will appear unchecked in the expanded mode.
    const radioName = `${Math.random()}`;
    return /*#__PURE__*/_jsx("div", {
      "data-selenium": this.seleniumKey,
      children: this.props.choices.map(choice => this.renderRadioOption(choice, radioName))
    });
  }
  renderButtonContents(label, buttonData = {}) {
    if (isEmpty(buttonData)) return /*#__PURE__*/_jsx("span", {
      children: label
    });
    const {
      color,
      icon,
      image,
      image_alt: imageAlt
    } = buttonData;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [this.renderIcon(icon, color), this.renderImage(image, imageAlt)]
    });
  }
  renderButtonLabelsAndHelpText(label, buttonData = {}) {
    if (isEmpty(buttonData)) return null;
    const {
      help_text: helpText
    } = buttonData;
    return /*#__PURE__*/_jsxs(UIFlex, {
      className: "m-top-1",
      justify: "center",
      children: [label, this.renderHelpText(helpText)]
    });
  }
  renderIconButton(choice) {
    const {
      value: fieldValue,
      preset,
      readOnly = false
    } = this.props;
    const [choiceValue, labelOrPresetName, labelProperties] = choice;
    const buttonPresets = preset && this.choiceButtonPresets[preset];
    const presetData = preset && buttonPresets ? buttonPresets[labelOrPresetName.toLowerCase()] : undefined;
    const label = preset && presetData ? presetData.label : labelOrPresetName;
    const buttonData = presetData || labelProperties;
    return /*#__PURE__*/_jsxs(View, {
      marginBlockEnd: "300",
      as: "div",
      children: [/*#__PURE__*/_jsx(SelectableBox, {
        type: "default",
        "aria-label": label,
        block: true,
        height: ICON_BUTTON_SIZE,
        onSelectedChange: () => this.onChangeValue(SyntheticEvent(choiceValue)),
        readOnly: readOnly,
        selected: choiceValue === fieldValue,
        width: ICON_BUTTON_SIZE,
        children: /*#__PURE__*/_jsx(IconWrapper, {
          children: this.renderButtonContents(label, buttonData)
        })
      }), this.renderButtonLabelsAndHelpText(label, buttonData)]
    });
  }
  renderButtons() {
    const {
      choices
    } = this.props;
    return /*#__PURE__*/_jsx(UIFlex, {
      align: "start",
      className: "m-top-2",
      justify: "start",
      wrap: "wrap",
      children: choices.map((choice, index) => /*#__PURE__*/_jsx(ButtonWrapper, {
        children: /*#__PURE__*/_jsx(UIFlex, {
          justify: this.getButtonFlexJustificationByIndex(index),
          children: this.renderIconButton(choice)
        })
      }, index))
    });
  }
  renderSelectValue(opt) {
    const text = opt.text || opt.buttonText || opt.value;
    if (text === undefined) return null;
    return /*#__PURE__*/_jsx(SanitizedLabel, {
      label: text
    });
  }
  renderSelectOption(opt) {
    const text = opt.text || opt.dropdownText || opt.value;
    return /*#__PURE__*/_jsx(SpacedDiv, {
      "data-selenium": `${this.seleniumKey}:opt:${text}`,
      paddingInline: "200",
      paddingBlock: "300",
      children: /*#__PURE__*/_jsx("div", {
        dangerouslySetInnerHTML: {
          __html: clean(text)
        },
        title: text
      })
    });
  }
  renderSelect() {
    const {
      choices,
      error,
      fieldKey,
      id,
      multiple = false,
      onFieldChange,
      placeholder,
      readOnly = false,
      reordering_enabled: reorderingEnabled = false,
      required = false,
      useOnDark,
      value
    } = this.props;
    const {
      isOpenSelect
    } = this.state;
    const _placeholder = placeholder || I18n.text(i18nKey('placeholder'));
    let fieldValue = value && value.selectedValue ? value.selectedValue : value;
    if (isObject(fieldValue)) fieldValue = undefined;
    const enableReordering = reorderingEnabled && Array.isArray(fieldValue) && fieldValue.length > 1;
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(UISelect, {
        buttonUse: useOnDark ? 'form-on-dark' : 'form',
        clearable: !required,
        "data-selenium": this.seleniumKey,
        "data-value": fieldValue,
        defaultValue: fieldValue,
        error: error,
        multi: multiple,
        onChange: this.onChangeValue,
        onOpenChange: this.onSelectOpenChange,
        open: isOpenSelect,
        optionRenderer: this.renderSelectOption,
        options: this.state.options,
        placeholder: _placeholder,
        readOnly: readOnly,
        value: fieldValue,
        valueRenderer: this.renderSelectValue
      }), enableReordering && /*#__PURE__*/_jsx(ReorderChoicesButton, {
        field: {
          choices,
          id,
          occurrence: {},
          type: CHOICE
        },
        fieldKey: fieldKey,
        onFieldChange: onFieldChange,
        value: fieldValue
      })]
    });
  }
  renderCheckboxOption(opt, checkboxName) {
    const {
      value,
      readOnly = false
    } = this.props;
    const [choiceValue, choiceLabel] = opt;
    let fieldValue = value;
    if (!Array.isArray(value)) {
      fieldValue = !isNullOrUndefined(value) ? [value] : [];
    }
    return /*#__PURE__*/_jsx(UICheckbox, {
      checked: fieldValue.includes(choiceValue),
      "data-selenium": `${this.seleniumKey}:opt:${choiceValue}`,
      name: checkboxName,
      onChange: evt => this.onChangeCheckbox(choiceValue, fieldValue, evt),
      readOnly: readOnly,
      size: "small",
      value: choiceValue,
      children: /*#__PURE__*/_jsx(SanitizedLabel, {
        label: choiceLabel
      })
    }, choiceValue);
  }
  renderCheckbox() {
    const {
      choices,
      reordering_enabled: reorderingEnabled = false
    } = this.props;
    if (reorderingEnabled) return this.renderSelect();

    // In the layout editor we want to display 2 edit forms next to each other
    // when in expanded mode. If there's a choice field we want each field instance
    // to have a completely unique checkbox field name.
    const checkboxName = `${Math.random()}`;
    return /*#__PURE__*/_jsx("div", {
      "data-selenium": this.seleniumKey,
      children: choices.map(choice => this.renderCheckboxOption(choice, checkboxName))
    });
  }
  render() {
    const {
      display = DISPLAY_TYPES.SELECT
    } = this.props;
    switch (display) {
      case DISPLAY_TYPES.RADIO:
        return this.renderRadio();
      case DISPLAY_TYPES.CHECKBOX:
        return this.renderCheckbox();
      case DISPLAY_TYPES.BUTTONS:
        return this.renderButtons();
      default:
        return this.renderSelect();
    }
  }
}
export default connector(ChoiceField);